<template>
  <div class="event">
    <DetailHeroSection
      :normalizedData="normalizedEventData"
      :navIndicator="navIndicator"
    />
    <div class="section__container py-4">
      <div class="loader" v-if="UIController.loader.contentLoading">
        <v-progress-circular indeterminate=""> </v-progress-circular>
        <p class="mt-5">Hold On! while we fetch the event for you.</p>
      </div>
      <div v-else>
        <v-card elevation="0" class="error-card" v-if="!hasDetails">
          <v-card-title class="error-card__title text-center dms700"
            >This Event Does Not Exist</v-card-title
          >
        </v-card>
        <div v-else>
          <v-row class="event-content">
            <!-- Main Content Column (8 columns) -->
            <v-col cols="12" lg="8" class="main-content">
              <div class="event-detail">
                <!-- Featured Image at top of left column -->
                <div
                  class="featured-image-container mb-12"
                  v-if="
                    eventData.featuredImage && eventData.featuredImage.trim()
                  "
                >
                  <v-img
                    :src="eventData.featuredImage"
                    class="event-detail__featured-image"
                    aspect-ratio="1.777"
                    cover
                  />
                </div>

                <!-- About Section -->
                <div
                  class="about-section"
                  v-if="finalHtml || processedDescription.length > 0"
                >
                  <!-- Parsed HTML Content -->
                  <div class="finalHtmlBody">
                    <div v-if="finalHtml" class="event-description-content">
                      <div class="js-toc-content prose" v-html="finalHtml" />
                    </div>
                    <div
                      v-else-if="processedDescription.length > 0"
                      class="event-description"
                    >
                      <div
                        v-for="(paragraph, index) in processedDescription"
                        :key="index"
                        class="description-paragraph"
                        v-html="paragraph"
                      />
                    </div>
                  </div>
                </div>

                <!-- Featured Experts Section using TestimonialCard -->
                <div
                  class="experts-section mt-6"
                  v-if="
                    eventData.featuredExperts &&
                    eventData.featuredExperts.length > 0
                  "
                >
                  <h3 class="experts-title headline">
                    {{
                      eventData.featuredExperts.length === 1
                        ? 'Featured Expert:'
                        : 'Featured Experts:'
                    }}
                  </h3>

                  <!-- Grid layout for testimonial cards -->
                  <div class="experts-grid">
                    <TestimonialCard
                      v-for="expert in eventData.featuredExperts"
                      :key="expert.id"
                      :presenter-description="
                        expert.description || expert.bio || ''
                      "
                      :presenter-image="expert.image"
                      :presenter-name="expert.name"
                      :presenter-title="expert.title"
                    />
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Sidebar Column (4 columns) -->
            <v-col cols="12" lg="4" class="sidebar-content">
              <div class="sticky-sidebar">
                <!-- Registration Card Component -->
                <RegisterSection :eventData="eventData" />
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>

    <!-- <v-snackbar
      v-model="UIController.snackbar.active"
      :location="UIController.snackbar.location"
      :text="UIController.snackbar.text"
      :color="UIController.snackbar.color"
    >
      {{ UIController.snackbar.text }}
    </v-snackbar> -->
  </div>
</template>

<script setup>
import DetailHeroSection from './DetailHeroSection.vue';
import RegisterSection from './RegisterSection.vue';
import TestimonialCard from './../resources/learningHub/TestimonialCard.vue';

import { computed, onMounted, reactive, ref } from 'vue';
import eventsService from '@/services/API/eventsService';
import { useRoute } from 'vue-router';
import DOMPurify from 'dompurify';

// Navigation breadcrumb
const navIndicator = ref([
  { text: 'Home', icon: 'fas fa-home', link: '/' },
  { text: 'Upcoming Events', link: '/events' },
  { text: 'Event Details', link: '#' },
]);

const UIController = reactive({
  loader: {
    contentLoading: true,
  },
  snackbar: {
    active: false,
    location: 'bottom right',
    text: '',
    color: 'danger',
  },
});

// Event data from new API structure
let eventData = reactive({});

// HTML parsing variables
const finalHtml = ref('');

// Computed property for normalized data
const normalizedEventData = computed(() => {
  if (!eventData || !eventData.eventDate) return {};

  try {
    const date = new Date(parseInt(eventData.eventDate));

    return {
      name: eventData.name,
      category: eventData.eventCategory,
      formattedDate: date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      formattedTime: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
      location: eventData.location,
      displayLocation:
        eventData.eventMode === 'Virtual'
          ? 'Virtual Event'
          : eventData.location || 'TBD',
      eventMode: eventData.eventMode,
      featuredImage: eventData.featuredImage,
    };
  } catch (error) {
    console.error('Error normalizing event data:', error);
    return {};
  }
});

// Utility functions
const eventDateComputed = (date) => {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'full',
    timeStyle: 'short',
    hour12: true,
  }).format(new Date(parseInt(date)));
};

const getInitials = (name) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

// Enhanced HTML parsing function (matching ShowTraining.vue)
const parseHtmlContent = async (content) => {
  if (!content) return '';

  // More comprehensive sanitization with additional formatting tags
  const parsedHtml = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
      'ul', 'ol', 'li', 'blockquote', 'a', 'img', 'div', 'span', 'pre', 'code', 
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'sub', 'sup', 'small',
      'mark', 'del', 'ins', 'cite', 'q', 'abbr', 'dfn', 'time', 'address'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'target', 'rel', 'class', 'id', 'style',
      'data-*', 'width', 'height', 'colspan', 'rowspan', 'datetime', 'cite'
    ],
    ALLOWED_ATTR_PREFIX: ['data-'],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    ALLOW_DATA_ATTR: true
  });
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(parsedHtml, 'text/html');
  
  // Preserve important formatting while removing problematic styles
  const allElements = doc.querySelectorAll('*');
  allElements.forEach((element) => {
    // Remove problematic alignment and positioning styles but keep text formatting
    if (element.style) {
      element.style.position = '';
      element.style.float = '';
      element.style.clear = '';
      element.style.textAlign = '';
      element.style.margin = '';
      element.style.padding = '';
    }
    
    // Remove problematic attributes but keep semantic ones
    element.removeAttribute('align');
    element.removeAttribute('valign');
  });
  
  // Add IDs to headings for better navigation
  const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings.forEach((heading) => {
    if (!heading.id && heading.textContent) {
      heading.id = heading.textContent
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]/g, '');
    }
  });

  // Ensure proper paragraph structure
  const body = doc.body;
  let html = body.innerHTML;
  
  // Convert double line breaks to paragraphs if not already wrapped
  html = html.replace(/\n\s*\n/g, '</p><p>');
  
  // Wrap orphaned text in paragraphs
  html = html.replace(/^(?!<[^>]+>)(.+)$/gm, '<p>$1</p>');
  
  // Clean up empty paragraphs
  html = html.replace(/<p>\s*<\/p>/g, '');
  
  // Fix nested paragraph issues
  html = html.replace(/<p>(\s*<(?:div|h[1-6]|ul|ol|blockquote)[^>]*>)/g, '$1');
  html = html.replace(/(<\/(?:div|h[1-6]|ul|ol|blockquote)>\s*)<\/p>/g, '$1');

  return html;
};

// Initial Setup
onMounted(async () => {
  document.getElementById('loaderSection')?.classList.add('d-none');

  try {
    const response = await eventsService.showEvent(useRoute().params.slug);

    // Map the new API response to eventData
    Object.assign(eventData, response.data);

    // Parse HTML content if description exists
    if (eventData.description) {
      let descriptionContent = '';

      // Handle if description is an array
      if (Array.isArray(eventData.description)) {
        descriptionContent = eventData.description.join('');
      } else if (typeof eventData.description === 'string') {
        descriptionContent = eventData.description;
      }

      // Parse the HTML content
      finalHtml.value = await parseHtmlContent(descriptionContent);
    }

    UIController.snackbar.text = 'Event fetched successfully';
    UIController.snackbar.color = 'success';
    UIController.snackbar.active = true;
    UIController.loader.contentLoading = false;

    console.log('Event Data:', eventData);
  } catch (error) {
    console.error('Error fetching event:', error);
    UIController.snackbar.text = 'Could not fetch event details';
    UIController.snackbar.color = 'error';
    UIController.snackbar.active = true;
    UIController.loader.contentLoading = false;
  }
});

// Computed Properties
const hasDetails = computed(() => {
  return Object.keys(eventData).length > 0;
});

const processedDescription = computed(() => {
  if (!eventData.description) {
    return [];
  }

  // Handle if description is an array
  if (Array.isArray(eventData.description)) {
    return eventData.description.map((desc) => {
      if (typeof desc === 'string') {
        // Clean up HTML and split into paragraphs if needed
        return desc.replace(/<strong>/g, '<b>').replace(/<\/strong>/g, '</b>');
      }
      return desc;
    });
  }

  // Handle if description is a string
  if (typeof eventData.description === 'string') {
    return [
      eventData.description
        .replace(/<strong>/g, '<b>')
        .replace(/<\/strong>/g, '</b>'),
    ];
  }

  return [];
});
</script>

<style scoped>
.event {
  background-color: #f7f8fc;
}
.event-content {
  max-width: 1200px;
  margin: 0 auto;
}

.main-content {
  padding-right: 2rem;
}

.sidebar-content {
  padding-left: 1rem;
}

.sticky-sidebar {
  position: sticky;
  top: 8rem;
  margin-bottom: 2rem;
}

.about-section {
  margin-bottom: 2rem;
}

.about-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-align: left;
  margin-top: 4rem;
  color: #333;
}

.highlight {
  color: #e74c3c;
}

.event-description {
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
  text-align: left !important;
}

.event-description * {
  text-align: left !important;
}

.event-description-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
  text-align: left !important;
}

.event-description-content * {
  text-align: left !important;
}

.description-paragraph {
  margin-bottom: 1rem;
  text-align: left !important;
}

.description-paragraph * {
  text-align: left !important;
}

.description-paragraph:last-child {
  margin-bottom: 0;
}

.experts-section {
  margin-top: 2rem;
}

.experts-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-top: 5rem;
  text-align: left;
  color: #333;
}

.headline {
  margin-bottom: 1rem;
}

/* Grid layout for expert cards */
.experts-grid {
  display: flex;
  flex-direction: column;
  /* flex-wrap: wrap; */
  gap: 1rem;
  margin-bottom: 2rem;
  text-align: left;
}

.featured-image-container {
  width: 100%;
  margin-bottom: 1.5rem;
}

.event-detail__featured-image {
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.mobile-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.error-card {
  background-color: var(--light-1);
  max-width: 500px;
  margin-inline: auto;
  margin-block: 2rem;
}

.loader {
  padding-block: 5rem;
  text-align: center;
}

/* Enhanced Prose styling that preserves text editor formatting */
.js-toc-content.prose {
  max-width: none;
  font-family: inherit;
  line-height: 1.7;
  color: #374151;
  /* Don't force text-align here - let it come from the content */
}



/* Headings - preserve editor formatting */
.js-toc-content.prose h1,
.js-toc-content.prose h2,
.js-toc-content.prose h3,
.js-toc-content.prose h4,
.js-toc-content.prose h5,
.js-toc-content.prose h6 {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.25;
  /* Preserve text-align from editor */
}

.js-toc-content.prose h1 { font-size: 2.25rem; }
.js-toc-content.prose h2 { font-size: 1.875rem; }
.js-toc-content.prose h3 { font-size: 1.5rem; }
.js-toc-content.prose h4 { font-size: 1.25rem; }
.js-toc-content.prose h5 { font-size: 1.125rem; }
.js-toc-content.prose h6 { font-size: 1rem; }

/* Paragraphs - preserve editor formatting */
.js-toc-content.prose p {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  line-height: 1.7;
  /* Let text-align come from inline styles or editor */
}

.js-toc-content.prose p:first-child {
  margin-top: 0;
}

.js-toc-content.prose p:last-child {
  margin-bottom: 0;
}

/* Text formatting - enhance to preserve editor styles */
.js-toc-content.prose strong,
.js-toc-content.prose b {
  font-weight: 700 !important; /* Ensure bold is visible */
  color: inherit; /* Preserve color from editor */
}

.js-toc-content.prose em,
.js-toc-content.prose i {
  font-style: italic !important;
}

.js-toc-content.prose u,
.js-toc-content.prose underline {
  text-decoration: underline !important;
}

.js-toc-content.prose s,
.js-toc-content.prose strike,
.js-toc-content.prose del {
  text-decoration: line-through !important;
}


.js-toc-content.prose *[align="left"] {
  text-align: left !important;
}

.js-toc-content.prose *[align="center"] {
  text-align: center !important;
}

.js-toc-content.prose *[align="right"] {
  text-align: right !important;
}

.js-toc-content.prose *[align="justify"] {
  text-align: justify !important;
}

/* Lists - preserve formatting */
.js-toc-content.prose ul,
.js-toc-content.prose ol {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  padding-left: 1.75rem;
}

.js-toc-content.prose ul {
  list-style-type: disc;
}

.js-toc-content.prose ol {
  list-style-type: decimal;
}

.js-toc-content.prose li {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  line-height: 1.7;
}

.js-toc-content.prose li > p {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
}

.js-toc-content.prose li:first-child {
  margin-top: 0;
}

.js-toc-content.prose li:last-child {
  margin-bottom: 0;
}

/* Nested lists */
.js-toc-content.prose ul ul,
.js-toc-content.prose ul ol,
.js-toc-content.prose ol ul,
.js-toc-content.prose ol ol {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
}

/* Links */
.js-toc-content.prose a {
  color: #3b82f6;
  text-decoration: underline;
  font-weight: 500;
}

.js-toc-content.prose a:hover {
  color: #1d4ed8;
}

/* Images */
.js-toc-content.prose img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin-top: 2rem;
  margin-bottom: 2rem;
  display: block;
}

/* Blockquotes */
.js-toc-content.prose blockquote {
  font-weight: 500;
  font-style: italic;
  color: #6b7280;
  border-left: 4px solid #d1d5db;
  padding-left: 1rem;
  margin-top: 1.6rem;
  margin-bottom: 1.6rem;
  quotes: "\201C""\201D""\2018""\2019";
}

.js-toc-content.prose blockquote p:first-of-type::before {
  content: open-quote;
}

.js-toc-content.prose blockquote p:last-of-type::after {
  content: close-quote;
}

/* Code */
.js-toc-content.prose code {
  color: #1f2937;
  font-weight: 600;
  font-size: 0.875rem;
  background-color: #f3f4f6;
  padding: 0.25rem 0.375rem;
  border-radius: 0.25rem;
}

.js-toc-content.prose code::before,
.js-toc-content.prose code::after {
  content: "`";
}

.js-toc-content.prose pre {
  color: #e5e7eb;
  background-color: #1f2937;
  overflow-x: auto;
  font-size: 0.875rem;
  line-height: 1.7;
  margin-top: 1.7rem;
  margin-bottom: 1.7rem;
  border-radius: 0.375rem;
  padding: 1rem;
}

.js-toc-content.prose pre code {
  background-color: transparent;
  border-width: 0;
  border-radius: 0;
  padding: 0;
  font-weight: 400;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}

.js-toc-content.prose pre code::before,
.js-toc-content.prose pre code::after {
  content: none;
}

/* Tables */
.js-toc-content.prose table {
  width: 100%;
  table-layout: auto;
  text-align: left;
  margin-top: 2rem;
  margin-bottom: 2rem;
  font-size: 0.875rem;
  line-height: 1.7;
  border-collapse: collapse;
}

.js-toc-content.prose thead {
  border-bottom: 1px solid #d1d5db;
}

.js-toc-content.prose thead th {
  color: #1f2937;
  font-weight: 600;
  vertical-align: bottom;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
}

.js-toc-content.prose tbody tr {
  border-bottom: 1px solid #e5e7eb;
}

.js-toc-content.prose tbody tr:last-child {
  border-bottom-width: 0;
}

.js-toc-content.prose tbody td {
  vertical-align: baseline;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
}

/* Horizontal rules */
.js-toc-content.prose hr {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin-top: 3rem;
  margin-bottom: 3rem;
}

/* Additional text formatting */
.js-toc-content.prose small {
  font-size: 0.875rem;
}

.js-toc-content.prose mark {
  background-color: #fef3c7;
  color: #92400e;
  padding: 0.125rem 0.25rem;
  border-radius: 0.125rem;
}

.js-toc-content.prose ins {
  text-decoration: underline;
  color: #059669;
  background-color: #d1fae5;
}

.js-toc-content.prose sub,
.js-toc-content.prose sup {
  font-size: 0.75rem;
  line-height: 0;
  position: relative;
  vertical-align: baseline;
}

.js-toc-content.prose sub {
  bottom: -0.25rem;
}

.js-toc-content.prose sup {
  top: -0.5rem;
}

/* Center alignment support */
.js-toc-content.prose center {
  text-align: center !important;
}

.js-toc-content.prose center * {
  text-align: center !important;
}



/* Override any forced left alignment for centered or right-aligned content */
.js-toc-content.prose [style*="text-align: center"],
.js-toc-content.prose [style*="text-align:center"] {
  text-align: center !important;
}

.js-toc-content.prose [style*="text-align: right"],
.js-toc-content.prose [style*="text-align:right"] {
  text-align: right !important;
}

.js-toc-content.prose [style*="text-align: justify"],
.js-toc-content.prose [style*="text-align:justify"] {
  text-align: justify !important;
}



/* Responsive Design */
@media (max-width: 1024px) {
  .main-content {
    padding-right: 1rem;
  }

  .sidebar-content {
    padding-left: 0;
    margin-top: 2rem;
    order: 2;
  }

  .main-content {
    order: 1;
  }

  .sticky-sidebar {
    position: static;
  }
}

@media (max-width: 768px) {
  .about-title {
    font-size: 1.5rem;
  }

  .main-content,
  .sidebar-content {
    padding: 0;
  }

  .experts-grid {
    flex-direction: column;
    gap: 1rem;
  }
}


</style>